import {UIGameManager} from '../../UIGameManager'
import {UIGameMapTileCornerHighlightController} from '../ui_game_map_highlight/UIGameMapTileCornerHighlightController'
import {GameStateEnums} from '../../../../../../shared/GameEnums'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {UIGameActionConfirmationParameters, UIHoverActionController} from '../../ui_game_hover_actions/UIHoverActionController'
import {UIGameMapHighlightParameters, UIGameMapHighlightView} from '../UIGameMapHighlightView'
import {PIXI} from '@colonist/pixi'
import {MapHoverController} from './UIGameMapHoverController'
import {BeginnerHintFormat, GameAction} from '@colonist/game-base-types'
import {ArrayUtils} from '@colonist/utils'

export class UIGameMapTileCornerHoverController extends UIGameMapTileCornerHighlightController implements MapHoverController {
    private readonly shouldHighlightOnlyOnHover: boolean
    private readonly hoverMechanicActions: UIHoverActionController[]

    constructor(uiGameManager: UIGameManager, shouldHighlightOnlyOnHover: boolean) {
        super(uiGameManager)
        this.shouldHighlightOnlyOnHover = shouldHighlightOnlyOnHover
        this.hoverMechanicActions = []
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.GameEnd: this.removeHighlights(); break
            case UIGameEventEnums.BeginnerHintReceived: this.beginnerHintReceived(data); break
            case UIGameEventEnums.BeginnerModeActionUpdated: this.onBeginnerModeActionUpdate(data); break
            case UIGameEventEnums.GameStateUpdated: this.destroyBeginnerHintView(); break
            case UIGameEventEnums.CanvasResized: this.onCanvasResize(); break
            default: break
        }
    }

    registerHoverAction(hoverAction: UIHoverActionController) {
        this.hoverMechanicActions.push(hoverAction)
    }

    onHoverStateChange(isHoverStateActive: boolean) {
        const beginnerHintActive = this.beginnerHintView != undefined
        if(beginnerHintActive) {
            this.destroyBeginnerHintView()
            this.beginnerHintReceived(this.beginnerHint)
        }

        if(!isHoverStateActive) {
            this.removeHighlights()
            return
        }

        const previousCorners = [...this.corners]
        const selectedIndex = this.confirmActionController.selectedLocationIndex

        this.updateCorners()

        const wasEmpty = this.highlightViews.length === 0
        const hasCorners = this.corners.length > 0

        if(wasEmpty && hasCorners) {
            this.updateHighlightsDifferentially([], selectedIndex)
        } else if(this.hasCornerChanges(previousCorners)) {
            this.updateHighlightsDifferentially(previousCorners, selectedIndex)
        } else if(hasCorners) {
            this.confirmActionController.setupInvisibleBackground(() => {
                this.startAnimation()
                this.hideBeginnerHintInformation()
            })
        }
        this.updateHighlightVisibility()
    }

    onHoverHighlightClick() {
        this.onHoverStateChange(true)
        this.endAnimation()
    }

    onHoverHighlightCancellation() {
        this.startAnimation()
    }

    onClickInvisibleBackground() {
        this.confirmActionController.cleanupConfirmation()
    }

    protected override highlight(highlightParameters: UIGameMapHighlightParameters, action: () => void) {
        const highlightView = new UIGameMapHighlightView(this.uiGameManager, highlightParameters, this.uiGameManager.mapController.mapView, action, false, this.shouldHighlightOnlyOnHover)
        if(this.shouldHighlightOnlyOnHover) {
            highlightView.displayOnlyOnHover()
        } else {
            const canAffordAction = this.getMechanicsAssociatedWithCorner(highlightParameters.index).some((mechanic) => mechanic.canTakeAction)
            highlightView.displayAlwaysOn(canAffordAction)
        }
        this.highlightViews.push(highlightView)
    }

    private updateCorners() {
        const newCorners: number[] = []

        this.hoverMechanicActions.forEach((mechanicAction: UIHoverActionController) => {
            if(!mechanicAction.isCornerAction) return
            newCorners.push(...mechanicAction.validLocations)
        })

        this.corners = newCorners
    }

    private hasCornerChanges(previousCorners: number[]): boolean {
        const cornerChanges = ArrayUtils.arraysEqual(previousCorners.sort(), this.corners.sort())
        return previousCorners.length !== this.corners.length || !cornerChanges
    }

    protected getActionForCornerHighlight(highlightParameters: UIGameMapHighlightParameters, p: PIXI.Point, index: number): () => void {
        return () => {
            if(!this.isAllowedPlacementIndex(index)) return this.uiGameManager.eventController.sendEvent(undefined, GameStateEnums.UIGameEventEnums.BeginnerModeForceOpenGuideBubble)

            this.uiGameManager.mapController.hoverController.onClickInvisibleBackground()

            const buttonSize = highlightParameters.size * 1.75 * this.buttonScaleForMobile
            const buttonX = p.x - buttonSize / 2
            const buttonY = p.y - (buttonSize * 1.5)
            this.destroyBeginnerHintView()
            this.uiGameManager.mapController.hoverController.onHoverHighlightClick()
            const cancellationAction = () => {
                this.uiGameManager.mapController.hoverController.onHoverHighlightCancellation()
            }
            const confirmationParameters: UIGameActionConfirmationParameters[] = []
            const mechanicsAssociatedWithCorner = this.getMechanicsAssociatedWithCorner(index)
            mechanicsAssociatedWithCorner.forEach((mechanic: UIHoverActionController) => confirmationParameters.push(mechanic.getConfirmationParameters(index)))
            this.confirmActionController.showActionConfirmation(highlightParameters, buttonX, buttonY, buttonSize, cancellationAction, confirmationParameters, index)
        }
    }

    protected override beginnerHintReceived(data: BeginnerHintFormat): void {
        const validGameActions = [GameAction.ConfirmBuildSettlement, GameAction.ConfirmBuildCity]
        if(!validGameActions.includes(data.type)) return
        super.beginnerHintReceived(data)
    }

    private getMechanicsAssociatedWithCorner(index: number): UIHoverActionController[] {
        const allowedMechanics: UIHoverActionController[] = []
        for(const mechanic of this.hoverMechanicActions) {
            if(!mechanic.isCornerAction) continue
            if(mechanic.validLocations.includes(index)) allowedMechanics.push(mechanic)
        }
        return allowedMechanics
    }

    private updateHighlightVisibility() {
        if(this.shouldHighlightOnlyOnHover) return
        for(const highlightView of this.highlightViews) {
            const canAffordAction = this.getMechanicsAssociatedWithCorner(highlightView.highlightedGameElementIndex).some((mechanic) => mechanic.canTakeAction)
            highlightView.displayAlwaysOn(canAffordAction)
        }
    }
}
